import { useCallback, useRef, useState } from 'react'
import { useTextToSpeech } from 'components/Common/AppTextToSpeech/AppTextToSpeech.component'
import { estimateSpeechDuration } from 'utils/audioUtils'

export const useVoicePlayback = () => {
  const { isSupported, isPlaying, speak, pause, resume, stop } = useTextToSpeech()
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [currentDuration, setCurrentDuration] = useState<number>(0)
  const playbackStartTimeRef = useRef<number | null>(null)
  const lastPlaybackRef = useRef<{ key: string; text: string } | null>(null)

  const startPlayback = useCallback(
    (key: string, text: string) => {
      if (!isSupported || !text) return

      const estimatedDuration = estimateSpeechDuration(text)
      setActiveKey(key)
      setCurrentDuration(estimatedDuration)
      playbackStartTimeRef.current = Date.now()
      lastPlaybackRef.current = { key, text }

      speak(text, {
        rate: 1,
        onEnd: () => {
          if (playbackStartTimeRef.current) {
            const elapsedMs = Date.now() - playbackStartTimeRef.current
            const elapsedSeconds = Math.max(1, Math.round(elapsedMs / 1000))
            setCurrentDuration(elapsedSeconds)
            playbackStartTimeRef.current = null
          }
          setActiveKey((prev) => (prev === key ? null : prev))
        },
        onError: () => {
          playbackStartTimeRef.current = null
          setActiveKey((prev) => (prev === key ? null : prev))
        },
      })
    },
    [isSupported, speak]
  )

  const togglePlayback = useCallback(
    (key: string, text: string) => {
      if (!isSupported || !text) {
        return
      }

      if (activeKey === key) {
        // If this message is already active, toggle between pause and resume
        if (isPlaying) {
          // Currently speaking: pause
          pause()
        } else {
          // Currently paused: resume
          resume()
        }
        return
      }

      // Start a new playback for this key
      startPlayback(key, text)
    },
    [activeKey, isSupported, isPlaying, pause, resume, startPlayback]
  )

  const clearPlayback = useCallback(() => {
    stop()
    setActiveKey(null)
    playbackStartTimeRef.current = null
    lastPlaybackRef.current = null
  }, [stop])

  return {
    isSupported,
    isPlaying,
    activeKey,
    duration: currentDuration,
    togglePlayback,
    clearPlayback,
  }
}
